<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Fixtures from "@/services/Fixtures";
import General from "@/services/General";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

const OPTIONAL_VENUE_FIELDS = [
  "venue_city",
  "venue_country",
  "venue_country_code",
  "venue_address",
  "venue_opened",
  "venue_capacity",
  "venue_surface",
  "venue_latitude",
  "venue_longitude",
  "teams",
];

export default {
  components: { validationMessages, flatPickr },

  props: {
    teamOptions: {
      type: Array,
      default: () => [],
    },
    stageOptions: {
      type: Array,
      default: () => [],
    },
    matchOptions: {
      type: Array,
      default: () => [],
    },
    standingOptions: {
      type: Array,
      default: () => [],
    },
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      stageId: "",
      homeTeam: "",
      awayTeam: "",
      date: "",
      time: "",
      round: "",
      venueId: "",
      venueName: "",
      venues: [],
      countries: [],
      showAddVenue: false,
      newVenueName: "",
      newVenueCity: "",
      newVenueCountry: "",
      newVenueCountryCode: "",
      newVenueAddress: "",
      newVenueOpened: "",
      newVenueCapacity: "",
      newVenueSurface: "",
      newVenueLatitude: "",
      newVenueLongitude: "",
      newVenueTeams: "",
      venueSubmitted: false,
      tryingToCreateVenue: false,
      submitted: false,
      tryingToSubmit: false,
      dateconfig: {
        enableTime: false,
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      timeconfig: {
        enableTime: true,
        noCalendar: true,
        time_24hr: true,
        minuteIncrement: 1,
        dateFormat: "H:i",
        allowInput: true,
      },
    };
  },

  computed: {
    selectedStage() {
      return (this.stageOptions || []).find((stage) => stage.stage_id === this.stageId) || null;
    },
    stageTeamOptions() {
      if (!this.selectedStage) {
        return [];
      }

      const tournamentId = this.selectedStage.tournament_id;
      const stageId = this.selectedStage.stage_id;
      const teamMap = {};

      (this.matchOptions || []).forEach((match) => {
        const sameStage = match.stage_id && match.stage_id === stageId;
        const sameTournament = tournamentId && match.tournament_id === tournamentId;
        if (!sameStage && !sameTournament) {
          return;
        }
        if (match.home_team_id) {
          teamMap[match.home_team_id] = {
            team_id: match.home_team_id,
            team_name: match.home_team_name || match.home_team_id,
            team_code: match.home_team_code || "",
          };
        }
        if (match.away_team_id) {
          teamMap[match.away_team_id] = {
            team_id: match.away_team_id,
            team_name: match.away_team_name || match.away_team_id,
            team_code: match.away_team_code || "",
          };
        }
      });

      (this.standingOptions || []).forEach((row) => {
        if (!tournamentId || row.tournament_id !== tournamentId || !row.team_id) {
          return;
        }
        teamMap[row.team_id] = {
          team_id: row.team_id,
          team_name: row.team_name || row.team_id,
          team_code: row.team_code || "",
        };
      });

      (this.teamOptions || []).forEach((team) => {
        if (!team.team_id || !teamMap[team.team_id]) {
          return;
        }
        teamMap[team.team_id] = Object.assign({}, teamMap[team.team_id], team);
      });

      // First match for a new stage/tournament: allow picking from all teams.
      const teams = Object.values(teamMap);
      if (!teams.length) {
        return this.teamOptions || [];
      }

      return teams.sort((a, b) =>
        String(a.team_name || "").localeCompare(String(b.team_name || ""))
      );
    },
  },

  watch: {
    stageId() {
      this.homeTeam = "";
      this.awayTeam = "";
    },
  },

  validations: {
    stageId: { required },
    homeTeam: { required },
    awayTeam: { required },
    date: { required },
    time: { required },
    newVenueName: {
      required(value) {
        if (!this.showAddVenue) {
          return true;
        }
        return Boolean(String(value || "").trim());
      },
    },
  },

  mounted() {
    this.getVenues();
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("add_sport_data_match");
    },

    openDatePicker() {
      if (this.$refs.datePicker && this.$refs.datePicker.fp) {
        this.$refs.datePicker.fp.open();
      }
    },

    openTimePicker() {
      if (this.$refs.timePicker && this.$refs.timePicker.fp) {
        this.$refs.timePicker.fp.open();
      }
    },

    formatError(error) {
      if (!error) return "Failed to create match";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    formatDatetime() {
      const date = String(this.date || "").trim();
      const time = String(this.time || "").trim();
      if (!date || !time) {
        return "";
      }
      const normalizedTime = time.length === 5 ? `${time}:00` : time;
      return `${date} ${normalizedTime}`;
    },

    async getVenues() {
      try {
        const response = await Fixtures.getVenues({
          provider: this.provider || "Azerion",
        });
        this.venues = response.data || [];
      } catch (error) {
        console.error("Error loading venues:", error);
        this.venues = [];
      }
    },

    onVenueChange() {
      const venue = this.venues.find((item) => item.venue_id === this.venueId);
      this.venueName = venue ? venue.venue_name : "";
    },

    toggleAddVenue() {
      this.showAddVenue = !this.showAddVenue;
      if (!this.showAddVenue) {
        this.resetNewVenueForm();
        return;
      }
      if (!this.countries.length) {
        this.getCountry();
      }
    },

    async getCountry() {
      try {
        const response = await General.getCountry();
        this.countries = this.preferredCountryOrder(response.data || [], "name");
      } catch (error) {
        this.countries = [];
      }
    },

    onVenueCountryChange() {
      const selected = this.countries.find(
        (country) => country.alpha3code === this.newVenueCountryCode
      );
      this.newVenueCountry = selected ? selected.name : "";
    },

    resetNewVenueForm() {
      this.newVenueName = "";
      this.newVenueCity = "";
      this.newVenueCountry = "";
      this.newVenueCountryCode = "";
      this.newVenueAddress = "";
      this.newVenueOpened = "";
      this.newVenueCapacity = "";
      this.newVenueSurface = "";
      this.newVenueLatitude = "";
      this.newVenueLongitude = "";
      this.newVenueTeams = "";
      this.venueSubmitted = false;
      this.tryingToCreateVenue = false;
    },

    buildVenuePayload() {
      const payload = {
        venue_name: String(this.newVenueName || "").trim(),
        provider: this.provider || "Azerion",
      };

      const optionalValues = {
        venue_city: this.newVenueCity,
        venue_country: this.newVenueCountry,
        venue_country_code: this.newVenueCountryCode,
        venue_address: this.newVenueAddress,
        venue_opened: this.newVenueOpened,
        venue_capacity: this.newVenueCapacity,
        venue_surface: this.newVenueSurface,
        venue_latitude: this.newVenueLatitude,
        venue_longitude: this.newVenueLongitude,
        teams: this.newVenueTeams,
      };

      OPTIONAL_VENUE_FIELDS.forEach((field) => {
        const value = String(optionalValues[field] || "").trim();
        if (value !== "") {
          payload[field] = value;
        }
      });

      return payload;
    },

    async createSportVenue() {
      this.venueSubmitted = true;
      this.$v.newVenueName.$touch();

      if (this.$v.newVenueName.$invalid) {
        return;
      }

      this.tryingToCreateVenue = true;
      const payload = this.buildVenuePayload();

      try {
        const response = await Fixtures.createSportVenue(payload);
        const created = response.data || {};
        await this.getVenues();

        if (created.venue_id) {
          this.venueId = created.venue_id;
          this.venueName = created.venue_name || payload.venue_name;
        }

        this.showAddVenue = false;
        this.resetNewVenueForm();
        Swal.fire("Success!", "Venue created successfully!", "success");
      } catch (error) {
        const err =
          error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : "";
        Swal.fire("Fail!", this.formatError(err) || "Failed to create venue", "warning");
      } finally {
        this.tryingToCreateVenue = false;
      }
    },

    async createSportMatch() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      if (this.homeTeam === this.awayTeam) {
        Swal.fire("Fail!", "Home and away team must be different", "warning");
        this.tryingToSubmit = false;
        return;
      }

      const payload = {
        stageId: this.stageId,
        homeTeam: this.homeTeam,
        awayTeam: this.awayTeam,
        datetime: this.formatDatetime(),
        provider: this.provider,
      };

      if (this.round !== "") payload.round = this.round;
      if (this.venueId) {
        payload.venueId = this.venueId;
        payload.venueName = this.venueName;
      }

      try {
        await Fixtures.addSportMatch(payload);
        Swal.fire("Success!", "Match created successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },

    resetForm() {
      this.stageId = "";
      this.homeTeam = "";
      this.awayTeam = "";
      this.date = "";
      this.time = "";
      this.round = "";
      this.venueId = "";
      this.venueName = "";
      this.showAddVenue = false;
      this.resetNewVenueForm();
      this.submitted = false;
      this.$v.$reset();
    },
  },
};
</script>

<template>
  <b-modal
    id="add_sport_data_match"
    title="Add Match"
    title-class="font-18"
    centered
    size="lg"
    @hidden="resetForm"
  >
    <form @submit.prevent="createSportMatch">
      <div class="form-group mb-3">
        <label for="match_stage_id">Stage <span class="text-danger">*</span></label>
        <b-form-select
          id="match_stage_id"
          v-model="stageId"
          :class="{ 'is-invalid': submitted && $v.stageId.$error }"
        >
          <option value="">Select stage</option>
          <option
            v-for="stage in stageOptions"
            :key="stage.stage_id"
            :value="stage.stage_id"
          >
            {{ stage.stage_name }} ({{ stage.tournament_name || stage.tournament_id }})
          </option>
        </b-form-select>
        <validationMessages v-if="submitted" :field="$v.stageId" />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="home_team">Home Team <span class="text-danger">*</span></label>
            <b-form-select
              id="home_team"
              v-model="homeTeam"
              :disabled="!stageId"
              :class="{ 'is-invalid': submitted && $v.homeTeam.$error }"
            >
              <option value="">{{ stageId ? "Select home team" : "Select stage first" }}</option>
              <option
                v-for="team in stageTeamOptions"
                :key="'home-' + team.team_id"
                :value="team.team_id"
              >
                {{ team.team_name }}
              </option>
            </b-form-select>
            <validationMessages v-if="submitted" :field="$v.homeTeam" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="away_team">Away Team <span class="text-danger">*</span></label>
            <b-form-select
              id="away_team"
              v-model="awayTeam"
              :disabled="!stageId"
              :class="{ 'is-invalid': submitted && $v.awayTeam.$error }"
            >
              <option value="">{{ stageId ? "Select away team" : "Select stage first" }}</option>
              <option
                v-for="team in stageTeamOptions"
                :key="'away-' + team.team_id"
                :value="team.team_id"
              >
                {{ team.team_name }}
              </option>
            </b-form-select>
            <validationMessages v-if="submitted" :field="$v.awayTeam" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="form-group mb-3">
            <label for="match_date">
              <i class="uil uil-calendar-alt me-1"></i>Date <span class="text-danger">*</span>
            </label>
            <div class="position-relative">
              <flat-pickr
                id="match_date"
                ref="datePicker"
                v-model="date"
                :config="dateconfig"
                class="form-control pe-4"
                :class="{ 'is-invalid': submitted && $v.date.$error }"
                name="date"
              />
              <i
                class="uil uil-calendar-alt position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                style="cursor: pointer; pointer-events: all;"
                @click="openDatePicker"
              ></i>
            </div>
            <validationMessages v-if="submitted" :field="$v.date" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group mb-3">
            <label for="match_time">
              <i class="uil uil-clock me-1"></i>Time <span class="text-danger">*</span>
            </label>
            <div class="position-relative">
              <flat-pickr
                id="match_time"
                ref="timePicker"
                v-model="time"
                :config="timeconfig"
                class="form-control pe-4"
                :class="{ 'is-invalid': submitted && $v.time.$error }"
                name="time"
              />
              <i
                class="uil uil-clock position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                style="cursor: pointer; pointer-events: all;"
                @click="openTimePicker"
              ></i>
            </div>
            <validationMessages v-if="submitted" :field="$v.time" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group mb-3">
            <label for="match_round">Round</label>
            <input id="match_round" v-model="round" type="number" min="0" class="form-control" />
          </div>
        </div>
      </div>

      <div class="form-group mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label for="match_venue" class="mb-0">Venue</label>
          <a
            href="javascript:void(0);"
            class="btn btn-success waves-effect waves-light btn-sm"
            @click="toggleAddVenue"
          >
            <i class="mdi mdi-plus me-2"></i>
            {{ showAddVenue ? "Cancel" : "Add new venue" }}
          </a>
        </div>
        <b-form-select id="match_venue" v-model="venueId" @change="onVenueChange">
          <option value="">Select venue (optional)</option>
          <option v-for="venue in venues" :key="venue.venue_id" :value="venue.venue_id">
            {{ venue.venue_name }}
          </option>
        </b-form-select>

        <div v-if="showAddVenue" class="mt-3 border rounded p-3 bg-light">
          <div class="form-group mb-3">
            <label for="new_venue_name">Venue name <span class="text-danger">*</span></label>
            <input
              id="new_venue_name"
              v-model="newVenueName"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': venueSubmitted && $v.newVenueName.$error }"
              placeholder="e.g. AFAS Stadion"
            />
            <validationMessages v-if="venueSubmitted" :field="$v.newVenueName" />
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="new_venue_city">City</label>
                <input
                  id="new_venue_city"
                  v-model="newVenueCity"
                  type="text"
                  class="form-control"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="new_venue_country">Country</label>
                <select
                  id="new_venue_country"
                  v-model="newVenueCountryCode"
                  class="form-select"
                  @change="onVenueCountryChange"
                >
                  <option value="">Select country (optional)</option>
                  <option
                    v-for="country in countries"
                    :key="country.alpha3code"
                    :value="country.alpha3code"
                  >
                    {{ country.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-8">
              <div class="form-group mb-3">
                <label for="new_venue_address">Address</label>
                <input
                  id="new_venue_address"
                  v-model="newVenueAddress"
                  type="text"
                  class="form-control"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label for="new_venue_opened">Opened</label>
                <input
                  id="new_venue_opened"
                  v-model="newVenueOpened"
                  type="text"
                  class="form-control"
                  placeholder="e.g. 1998"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label for="new_venue_capacity">Capacity</label>
                <input
                  id="new_venue_capacity"
                  v-model="newVenueCapacity"
                  type="text"
                  class="form-control"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label for="new_venue_surface">Surface</label>
                <input
                  id="new_venue_surface"
                  v-model="newVenueSurface"
                  type="text"
                  class="form-control"
                  placeholder="e.g. grass"
                />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-3">
                <label for="new_venue_teams">Team ID</label>
                <input
                  id="new_venue_teams"
                  v-model="newVenueTeams"
                  type="text"
                  class="form-control"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="new_venue_latitude">Latitude</label>
                <input
                  id="new_venue_latitude"
                  v-model="newVenueLatitude"
                  type="text"
                  class="form-control"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="new_venue_longitude">Longitude</label>
                <input
                  id="new_venue_longitude"
                  v-model="newVenueLongitude"
                  type="text"
                  class="form-control"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          <a
            href="javascript:void(0);"
            class="btn btn-primary waves-effect waves-light"
            :class="{ disabled: tryingToCreateVenue }"
            @click="createSportVenue"
          >
            <b-spinner v-show="tryingToCreateVenue" small class="me-2"></b-spinner>
            Save venue
          </a>
        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createSportMatch" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
